// RSA-4096 SSH key generation via the native Web Crypto API (OS-backed CSPRNG).
// Private key is exported as PKCS#8 PEM (compatible with OpenSSH 7.8+).
// Public key is encoded into OpenSSH `ssh-rsa` wire format.
//
// CRITICAL (plan FINDING-1): SSH `mpint` is a signed big-endian integer. If the
// high bit of the first byte is set, a leading 0x00 MUST be prepended, otherwise
// the value is read as negative and the key is rejected by ssh/servers. RSA-4096
// moduli almost always have that bit set.
import type { SshKeyPair } from './ssh-types';

const RSA_MODULUS_LENGTH = 4096;

function base64urlToBytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/** 4-byte big-endian length prefix. */
function uint32(n: number): Uint8Array {
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

/** SSH `string`: uint32 length + raw bytes. */
function sshString(bytes: Uint8Array): Uint8Array {
  return concatBytes([uint32(bytes.length), bytes]);
}

/** SSH `mpint`: strip leading zeros, prepend 0x00 if the top bit is set, then length-prefix. */
function sshMpint(raw: Uint8Array): Uint8Array {
  let start = 0;
  while (start < raw.length - 1 && raw[start] === 0) start++;
  let value = raw.subarray(start);
  if (value.length > 0 && (value[0] & 0x80) !== 0) {
    value = concatBytes([new Uint8Array([0x00]), value]);
  }
  return concatBytes([uint32(value.length), value]);
}

function toPem(der: ArrayBuffer, label: string): string {
  const b64 = bytesToBase64(new Uint8Array(der));
  const wrapped = b64.match(/.{1,64}/g)?.join('\n') ?? b64;
  return `-----BEGIN ${label}-----\n${wrapped}\n-----END ${label}-----\n`;
}

/** Builds the `ssh-rsa AAAA...` line and the SHA256 fingerprint from the public JWK. */
async function encodeRsaPublic(
  jwk: JsonWebKey,
  comment: string,
): Promise<{ publicKey: string; fingerprint: string }> {
  if (!jwk.n || !jwk.e) {
    throw new Error('encodeRsaPublic: JWK missing modulus or exponent');
  }
  const e = base64urlToBytes(jwk.e);
  const n = base64urlToBytes(jwk.n);

  const algorithm = new TextEncoder().encode('ssh-rsa');
  const blob = concatBytes([sshString(algorithm), sshMpint(e), sshMpint(n)]);

  const publicKey = `ssh-rsa ${bytesToBase64(blob)}${comment ? ` ${comment}` : ''}`;

  // Uint8Array.from yields an ArrayBuffer-backed view (TS 5.7 typed-array generics).
  const digest = await crypto.subtle.digest('SHA-256', Uint8Array.from(blob));
  const fingerprint = `SHA256:${bytesToBase64(new Uint8Array(digest)).replace(/=+$/, '')}`;

  return { publicKey, fingerprint };
}

export async function generateRsaKeyPair(comment: string): Promise<SshKeyPair> {
  const keyPair = (await crypto.subtle.generateKey(
    {
      name: 'RSASSA-PKCS1-v1_5',
      modulusLength: RSA_MODULUS_LENGTH,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]), // 65537
      hash: 'SHA-256',
    },
    true,
    ['sign', 'verify'],
  )) as CryptoKeyPair;

  const pkcs8 = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
  const jwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);

  const { publicKey, fingerprint } = await encodeRsaPublic(jwk, comment);

  return {
    type: 'rsa',
    publicKey,
    privateKey: toPem(pkcs8, 'PRIVATE KEY'),
    fingerprint,
    privateFilename: 'id_rsa',
    publicFilename: 'id_rsa.pub',
  };
}

// Exposed for unit tests (mpint sign-padding is the highest-risk detail).
export const __test = { sshMpint, sshString, encodeRsaPublic, base64urlToBytes };
